import { Component } from '@theme/component';
import { ProductSelectEvent, StandardEvents } from '@shopify/events';

/**
 * Switches the primary product image when a custom gallery thumbnail is selected.
 *
 * @typedef {object} ExternalProductGalleryRefs
 * @property {HTMLImageElement} mainImage
 * @property {HTMLButtonElement[]} thumbnail
 * @extends Component<ExternalProductGalleryRefs>
 */
class ExternalProductGallery extends Component {
  requiredRefs = ['mainImage', 'thumbnail'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener('product-gallery:select', this.#handleProductSelection, {
      signal: this.#abortController.signal,
    });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleVariantSelection, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @param {PointerEvent} event */
  handleSelect(event) {
    const target = event.target;
    const button = target instanceof Element ? target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    this.#selectThumbnail(button);
  }

  /** @param {CustomEvent<{index: number}>} event */
  #handleProductSelection = (event) => this.select(event.detail.index);

  /**
   * Selects the custom gallery image mapped to the newly selected Shopify variant.
   * @param {ProductSelectEvent} event
   */
  #handleVariantSelection = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const variantTitle = detail?.resource?.title;
        if (!variantTitle) return;

        const button = this.refs.thumbnail.find((thumbnail) => thumbnail.dataset.variantTitle === variantTitle);
        if (button) this.#selectThumbnail(button);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[external-product-gallery] Event promise rejected:', error);
      });
  };

  /** Selects a gallery image using its zero-based position. @param {number} index */
  select(index) {
    const button = this.refs.thumbnail?.[index];
    if (button instanceof HTMLButtonElement) this.#selectThumbnail(button);
  }

  /** @param {HTMLButtonElement} button */
  #selectThumbnail(button) {
    const source = button.dataset.source;
    if (!source) return;

    this.refs.mainImage.src = source;
    this.refs.mainImage.alt = button.dataset.alt ?? '';

    for (const thumbnail of this.refs.thumbnail) {
      const isSelected = thumbnail === button;
      thumbnail.classList.toggle('is-selected', isSelected);
      thumbnail.setAttribute('aria-current', isSelected ? 'true' : 'false');
    }
  }
}

if (!customElements.get('external-product-gallery')) {
  customElements.define('external-product-gallery', ExternalProductGallery);
}
